import type { ModalProps } from '@/components/modal/modal';
import { AffiliationAvailabilityModal } from '@/components/affiliation/AffiliationAvailabilityModal';
import { useAuth } from '@/contexts/auth-context';
import { useCurrentBrandQuery } from '@/hooks/queries/use-current-brand-query';
import { useEffect, useRef, useState, type ReactNode } from 'react';

/** Read the server decision; never infer availability from billing, plan or platform. */
export function useAffiliationAvailability(enabled = true) {
  const query = useCurrentBrandQuery(enabled);
  const availability = query.isError ? undefined : query.data?.affiliationAvailability;
  return {
    availability,
    loading: query.isPending || (query.isFetching && !availability),
    error: query.isError || (query.isSuccess && !availability),
    retry: () => void query.refetch(),
  };
}

/** Blocks direct/deep-linked wizards before their forms mount. Reconnection keeps its existing flow. */
export function AffiliationAvailabilityBoundary({
  isOpen,
  onClose,
  children,
}: {
  isOpen: boolean;
  onClose: () => void;
  children: ReactNode;
}) {
  const state = useAffiliationAvailability(isOpen);
  if (!isOpen) return null;
  if (!state.loading && !state.error && state.availability?.available) {
    return children;
  }
  return (
    <AffiliationAvailabilityModal
      isOpen
      onClose={onClose}
      reconnect={state.availability?.reason === 'shopify_reconnect_required'}
      loading={state.loading}
      error={state.error}
      onRetry={state.retry}
    />
  );
}

/** Runs availability before quotas or any mutation/form, preserving the clicked action while loading. */
export function useAffiliationAvailabilityGate() {
  const { user } = useAuth();
  const brandId = user?.currentBrandId;
  const state = useAffiliationAvailability();
  const [open, setOpen] = useState(false);
  const finalFocusEl = useRef<ModalProps['finalFocusEl']>(undefined);
  const pendingAction = useRef<{ brandId: string | null | undefined; action: () => void } | null>(
    null
  );
  const close = () => {
    pendingAction.current = null;
    setOpen(false);
  };
  const canContinue = !state.loading && !state.error && state.availability?.available === true;
  useEffect(() => {
    if (pendingAction.current && pendingAction.current.brandId !== brandId) {
      pendingAction.current = null;
      setOpen(false);
      return;
    }
    if (open && canContinue && pendingAction.current) {
      const { action } = pendingAction.current;
      pendingAction.current = null;
      setOpen(false);
      action();
    }
  }, [open, canContinue, brandId]);
  return {
    guard: (action: () => void, focusTarget?: ModalProps['finalFocusEl']) => {
      if (canContinue) action();
      else {
        finalFocusEl.current = focusTarget;
        pendingAction.current = { brandId, action };
        setOpen(true);
      }
    },
    modal: (
      <AffiliationAvailabilityModal
        isOpen={open}
        onClose={close}
        finalFocusEl={finalFocusEl.current}
        reconnect={state.availability?.reason === 'shopify_reconnect_required'}
        loading={state.loading}
        error={state.error}
        onRetry={state.retry}
      />
    ),
  };
}
